import * as React from 'react';
import Popconfirm from 'antd/lib/popconfirm';
import PermissionedActionButton from './../HOCs/PermissionedActionButton';
import { DeleteOutlined } from '@ant-design/icons';
import { DefaultUserGroupId, UserGroup } from '../../types';
import { antRed } from './../styles/globalStyles';
import { useDispatch } from 'react-redux';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { DeleteCustomGroupRequest, tryUpdateOrgSettings } from '@/actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface RemoveGroupButtonProps {
    selectedGroup?: UserGroup;
    orgId: string;
    onGroupDeleted: () => void;
}

const RemoveGroupButton: React.FC<RemoveGroupButtonProps> = (props) => {
    const { selectedGroup, orgId } = props;
    const dispatch = useDispatch();
    const isError = useError(OrgSettingsUpdatable.DeleteCustomGroup, selectedGroup.id);
    const isLoading = useIsLoading(OrgSettingsUpdatable.DeleteCustomGroup, selectedGroup.id);
    const isSuccess = useUpdated(OrgSettingsUpdatable.DeleteCustomGroup, selectedGroup.id);
    const [isPopconfirmVisible, setIsPopconfirmVisible] = React.useState(false);

    const deleteGroup = async () => {
        const request: DeleteCustomGroupRequest = {
            orgId: orgId,
            groupId: selectedGroup.id,
        };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.DeleteCustomGroup, request, selectedGroup.id));
    };

    React.useEffect(() => {
        if (isError) {
            globalMessage.error(isError);
        }
    }, [isError]);

    React.useEffect(() => {
        if (isSuccess) {
            setIsPopconfirmVisible(false);
            globalMessage.success('Group successfully deleted');
            props.onGroupDeleted();
        }
    }, [isSuccess]);

    return (
        <Popconfirm
            open={isPopconfirmVisible}
            placement="left"
            title={'Are you sure you want to delete this group?'}
            description={'Access to the user actions defined in this group will be revoked for all group members'}
            onConfirm={deleteGroup}
            okText="Yes, delete"
            okButtonProps={{ loading: isLoading }}
            cancelText="Cancel"
            onCancel={() => setIsPopconfirmVisible(false)}
        >
            <PermissionedActionButton
                type="link"
                icon={<DeleteOutlined style={{ color: antRed.red4 }} />}
                style={{ color: antRed.red5, display: 'flex', alignItems: 'center' }}
                actionType={RunwayUserAction.UACreateEditDeleteCustomGroup}
                onClick={() => setIsPopconfirmVisible(true)}
                disabled={selectedGroup.id === DefaultUserGroupId.Pilot}
            >
                Delete group
            </PermissionedActionButton>
        </Popconfirm>
    );
};

export default RemoveGroupButton;
